import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Dialog,
  Icon,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  Switch,
  Textfield,
  StateParams
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  styled
} from '@mui/material'
import { DataService, DataServiceProps } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import { useFormatMessage } from 'hooks/format-message'
import useCustomFields from 'hooks/useCustomFields'
import { useSectionPermission } from 'hooks/usePermission'
import useTags from 'hooks/useTags'
import { getActiveMerchantUuid, isGroupMode } from 'lib/merchant-group'
import { AutoCompleteBillingsParams } from 'models/Billings/AutoComplete'
import { useRouter } from 'next/router'
import IdentifyInternal from 'templates/Customers/Modals/BodyModalNewChargeOrRecurrence/Components/OptionsAdditionals/components/IdentifyInternal/IdentifyInternal/IdentifyInternal'
import { customerFormSchema } from 'templates/v2/Data/components/Form/schema'

import CreateGroupModal from 'components/CreateGroupModal'
import CustomFieldRenderer from 'components/CustomFieldRenderer'
import {
  CanCreateResult,
  checkCanCreate,
  ConflictRecordList,
  INACTIVE_STATUSES
} from 'components/DuplicateCustomer'
import ExtraFieldsManager, {
  ExtraFieldItem
} from 'components/ExtraFieldsManager'
import { useExtraFields } from 'components/ExtraFieldsManager/useExtraFields'
import GroupOption, { toGroupOption } from 'components/GroupOption'
import IconContainer from 'components/IconContainer/IconContainer'
import SparkleIcon from 'components/Icons/SparkleIcon'
import InputController from 'components/InputController'
import { ModalNewCustomerProps } from 'components/Modal/ModalNewCustomer/ModalNewCustomer'
import { PhoneInput } from 'components/PhoneInput'
import StateAutocomplete from 'components/StateAutocomplete'
import TagsPopover from 'components/TagsPopover/TagsPopover'

import { Debouncer, onlyNumber, UFOptions } from 'utils'
import { getCEPData } from 'utils/cepService'
import { COUNTRIES } from 'utils/countries'
import { normalizeUploadSubmitValue } from 'utils/customFieldUpload'
import { normalizeDocument } from 'utils/document'
import { setValues } from 'utils/SetValues'
import { prepareFormDataWithTags } from 'utils/tags'
import { isMobileViewport, track } from 'utils/tracking'
import { toastProps } from 'utils/types/toast-props'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import { CustomersBodyModalForm } from '../interface'
import { createDynamicCustomerSchema } from '../schema'

const StyledMenuItem = styled(MenuItem)(() => ({
  display: 'flex',
  fontSize: 14,
  color: '#646464',
  gap: '8px',
  paddingTop: '6px',
  paddingBottom: '6px',
  borderRadius: '6px'
}))

const MenuItemsWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 180
}))

export type CustomerCreatedData = {
  uuid: string
  name: string
  document?: string
  email?: string
  phone?: string
}

type Params = {
  onClose: (get: boolean) => void
  defaultValues?: Partial<CustomersBodyModalForm>
  onSubmit?: (value: CustomersBodyModalForm) => void
  isFromAutocomplete?: boolean
  onCustomerCreated?: (customer: CustomerCreatedData) => void
  // SC-10999: seletor "Empresa" da criação na visão multi-empresas
  companySelector?: React.ReactNode
} & Omit<
  ModalNewCustomerProps,
  'isOpenModalNewCustomer' | 'setIsOpenModalNewCustomer'
>

type DialogProps = {
  isOpen: boolean
  description: string
  title: string
  customerUuid: string
  action: () => void
}

// Opção sentinela que abre o modal "Novo grupo" (mesmo padrão do link de cadastro).
const CREATE_NEW_GROUP = '__create_new_group__'

const newGroupButton = (
  <Stack width="100%">
    <Button
      fullWidth
      variantButton="ghost"
      sx={{ justifyContent: 'flex-start' }}
    >
      <Stack direction="row" gap="5px" alignItems="center">
        <Icon
          icon="add"
          sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
        />
        <Typography
          sx={{
            color: theme.palette.neutral[400],
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Novo grupo
        </Typography>
      </Stack>
    </Button>
  </Stack>
)

const dialogDefaultValues = {
  isOpen: false,
  description: '',
  title: '',
  customerUuid: '',
  action: () => ({})
}

type DuplicateDialogState = CanCreateResult & {
  isOpen: boolean
  dataForm: CustomersBodyModalForm | null
}

const duplicateDialogDefaultValues: DuplicateDialogState = {
  isOpen: false,
  canCreate: true,
  reason: null,
  allowDuplicate: false,
  records: [],
  dataForm: null
}

const isEmptyCustomValue = (value: any) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

const isFilled = (value: any) =>
  value !== null && value !== undefined && String(value).trim() !== ''

type HandleToggleDialogParams = {
  action?: () => void
  title?: string
  description?: string
  customerUuid?: string
}

export function RenderBodyCustomerModal({
  onClose,
  setRefresh,
  onSubmit,
  defaultValues = {},
  isFromAutocomplete = false,
  onCustomerCreated,
  companySelector
}: Params) {
  const { f } = useFormatMessage()
  const { push } = useRouter()
  const { switchAccount } = useAuthContext()

  // SC-10999: a ficha do cliente é por empresa. Criado pela visão do grupo, o
  // cliente é da empresa do formulário: troca para ela e abre a ficha lá.
  const openCreatedCustomer = (uuid: string) => {
    const merchantUuid = isGroupMode() ? getActiveMerchantUuid() : undefined
    if (merchantUuid) {
      switchAccount({ merchantUuid }, { redirectTo: `/clientes/${uuid}` })
      return
    }
    push(`clientes/${uuid}`)
  }
  const [uploadIsLoading, setUploadIsLoading] = useState<boolean>(false)
  const [documentOptions, setDocumentOptions] = useState({
    document: '',
    loading: false
  })
  const { customFields, generateValidationSchema } = useCustomFields({
    contextType: 'internal_customer',
    fieldNamePrefix: 'extraField',
    autoLoad: true,
    excludeFieldTypes: ['accept']
  })

  const { items, handleItemsChange, getCustomFieldValues } = useExtraFields({
    baseSchema: customerFormSchema
  })

  const baseSchema = useMemo(() => {
    return createDynamicCustomerSchema([])
  }, [])

  const processAllConfiguredFields = (formData: any) => {
    let customFieldValues: Array<{
      custom_field_uuid: string
      value: any
    }> = []

    const cleanedFormData = { ...formData }

    customFields.forEach((field) => {
      const fieldName = `extraField_${field.uuid}`
      const fieldValue = formData[fieldName]

      let processedValue = fieldValue

      // Processar valor baseado no tipo do campo
      if (field.field_type === 'checkbox') {
        if (Array.isArray(fieldValue) && fieldValue.length > 0) {
          processedValue = fieldValue.map((value) => {
            const option = field.options?.find((opt) => opt.value === value)
            return option
              ? { label: option.label, value: option.value }
              : { label: value, value }
          })
        } else {
          processedValue = []
        }
      } else if (field.field_type === 'radio') {
        if (fieldValue) {
          const option = field.options?.find((opt) => opt.value === fieldValue)
          processedValue = option
            ? { label: option.label, value: option.value }
            : String(fieldValue)
        } else {
          processedValue = ''
        }
      } else if (field.field_type === 'upload') {
        // Upload envia o uuid do documento, ou a lista de uuids quando o campo
        // aceita vários arquivos
        processedValue = normalizeUploadSubmitValue(fieldValue)
      } else {
        processedValue = fieldValue ? String(fieldValue) : ''
      }

      customFieldValues.push({
        custom_field_uuid: field.uuid,
        value: processedValue
      })

      delete cleanedFormData[fieldName]
    })

    const extraFieldsCustomValues = getCustomFieldValues(formData)
    customFieldValues = [...customFieldValues, ...extraFieldsCustomValues]

    items.forEach((field) => {
      const fieldName = `extraField_${field.uuid}`
      delete cleanedFormData[fieldName]
    })

    return { customFieldValues, cleanedFormData }
  }

  const dynamicSchema = useMemo(() => {
    return generateValidationSchema(baseSchema)
  }, [baseSchema, generateValidationSchema])

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    clearErrors,
    setError
  } = useForm<CustomersBodyModalForm>({
    defaultValues: {
      ...defaultValues,
      create_user: false,
      is_lead: false,
      foreign: false,
      country_code: '',
      state: defaultValues?.state || null
    } as CustomersBodyModalForm,
    resolver: yupResolver(dynamicSchema, {
      abortEarly: false // Mostra todos os erros de validação de uma vez
    })
  })
  const [groups, setGroups] = useState<AutoCompleteBillingsParams[]>([])
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)
  const [contractMissingCustomFields, setContractMissingCustomFields] =
    useState<
      Array<{
        uuid: string
        title: string
        field_type: string
        options?: Array<{ label: string; value: string }>
      }>
    >([])
  const [contractEmptyCustomFields, setContractEmptyCustomFields] = useState<
    Array<{ uuid: string; title: string }>
  >([])
  const [contractRequiredCustomFields, setContractRequiredCustomFields] =
    useState<Array<{ uuid: string; title: string; field_type: string }>>([])
  const [contractDialogMissingLabels, setContractDialogMissingLabels] =
    useState<string[]>([])
  const [contractBillingFields, setContractBillingFields] = useState<string[]>(
    []
  )
  const [contractMissingStandardFields, setContractMissingStandardFields] =
    useState<Array<{ field: string; label: string }>>([])
  const [contractRequiredStandardFields, setContractRequiredStandardFields] =
    useState<Array<{ field: string; label: string }>>([])
  const [contractModels, setContractModels] = useState<
    Array<{ label: string; value: string }>
  >([])
  const [loadFetch, setLoadFetch] = useState<boolean>(false)
  const [dialog, setDialog] = useState<DialogProps>(dialogDefaultValues)
  const [duplicateDialog, setDuplicateDialog] = useState<DuplicateDialogState>(
    duplicateDialogDefaultValues
  )
  const [selectedConflictUuid, setSelectedConflictUuid] = useState<string>('')
  const [duplicateDialogLoading, setDuplicateDialogLoading] =
    useState<boolean>(false)
  // Fallback liberal: sessão sem o claim de RBAC libera e deixa o backend barrar.
  const canArchiveCustomers = useSectionPermission('customers:archive')
  const [stateError, setStateError] = useState<string | undefined>()
  const [stateValue, setStateValue] = useState<StateParams | null>(null)
  const [, setCepError] = useState<string | undefined>()
  const [cepLoading, setCepLoading] = useState<boolean>(false)
  const isSubmittingRef = useRef(false)
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<null | HTMLElement>(
    null
  )
  const [foreignAnchorEl, setForeignAnchorEl] = useState<null | HTMLElement>(
    null
  )
  const COUNTRY_OPTIONS = COUNTRIES
  const { document } = watch()
  const isMobile = useMediaQuery('(max-width:800px)')

  const tributaryRegimeOptions = [
    { label: 'Simples Nacional', value: '1' },
    { label: 'Lucro Presumido', value: '2' },
    { label: 'Lucro Real', value: '3' }
  ]

  // Hook para gerenciar tags
  const { tags: availableTags } = useTags()

  const handleStateError = (error: string | undefined) => {
    setStateError(error || '')
  }

  const handleStateValue = (value: StateParams | null) => {
    setStateValue(value)
  }
  const closeDuplicateDialog = () => {
    setDuplicateDialog(duplicateDialogDefaultValues)
    setSelectedConflictUuid('')
    setDuplicateDialogLoading(false)
  }

  // Desistir do dialog é diferente de fechá-lo: o submit que abriu o dialog ficou
  // pendurado em loadFetch/isSubmittingRef, e sem destravar os dois o formulário
  // fica em loading para sempre e não aceita um novo submit. Os outros pontos que
  // chamam closeDuplicateDialog seguem para finishWithCustomer ou para o create,
  // que cuidam desse estado por conta própria — por isso o reset vive aqui e não
  // dentro de closeDuplicateDialog.
  const dismissDuplicateDialog = () => {
    // Mesma guarda que o botão Cancelar tinha (disabled={duplicateDialogLoading}):
    // com requisição em voo, sair deixaria o PATCH/unarchive terminar sozinho depois
    // do dialog já ter sumido da tela.
    if (duplicateDialogLoading) return

    closeDuplicateDialog()
    setLoadFetch(false)
    isSubmittingRef.current = false
  }

  const handleToggleDialog = ({
    customerUuid = '',
    description = '',
    title = '',
    action = () => ({})
  }: HandleToggleDialogParams) => {
    const dialogIsOpen = dialog.isOpen
    const options = dialogIsOpen
      ? dialogDefaultValues
      : { isOpen: !dialogIsOpen, description, title, action, customerUuid }
    setDialog(options)
  }

  const buildCustomerPayload = (dataForm: CustomersBodyModalForm) => {
    const { customFieldValues, cleanedFormData } =
      processAllConfiguredFields(dataForm)

    const hasAddressData = !!(
      cleanedFormData.city ||
      cleanedFormData.complement ||
      cleanedFormData.neighborhood ||
      cleanedFormData.numberHome ||
      cleanedFormData.postalCode ||
      stateValue?.value ||
      cleanedFormData.street
    )

    // Processar internal identifiers para metadata (seguindo padrão de charges)
    let metadata: Record<string, any> | null = null
    if (
      dataForm.hasIdentifyInternal &&
      dataForm.internalIdentifiers &&
      dataForm.internalIdentifiers.length > 0
    ) {
      const metadataObj: Record<string, any> = {}
      dataForm.internalIdentifiers.forEach(({ name, value }) => {
        if (name && value) {
          metadataObj[name] = value
        }
      })
      if (Object.keys(metadataObj).length > 0) {
        metadata = metadataObj
      }
    }

    const tributaryRegimeValue = cleanedFormData.tributary_regime

    const isForeign = !!cleanedFormData.foreign
    const stateForPayload = isForeign
      ? (cleanedFormData.state as any)?.value || ''
      : stateValue?.value?.trim() || ''
    const hasForeignAddressData = !!(
      isForeign &&
      (cleanedFormData.city ||
        cleanedFormData.complement ||
        cleanedFormData.neighborhood ||
        cleanedFormData.numberHome ||
        cleanedFormData.postalCode ||
        stateForPayload ||
        cleanedFormData.street ||
        cleanedFormData.country_code)
    )
    const shouldIncludeAddress = isForeign
      ? hasForeignAddressData
      : hasAddressData

    const data: any = {
      document: cleanedFormData.document,
      email: cleanedFormData.email,
      name: cleanedFormData.name,
      nickname: cleanedFormData.nickname,
      phone: cleanedFormData.phone,
      foreign: isForeign,
      create_user: cleanedFormData?.create_user,
      category: cleanedFormData?.is_lead ? 'lead' : 'active_customer',
      source: 1,
      ...(shouldIncludeAddress && {
        addresses_attributes: [
          {
            city: cleanedFormData.city,
            complement: cleanedFormData.complement,
            neighborhood: cleanedFormData.neighborhood,
            number: cleanedFormData.numberHome,
            postal_code: cleanedFormData.postalCode,
            state: stateForPayload,
            street: cleanedFormData.street,
            ...(isForeign && cleanedFormData.country_code
              ? {
                  country_code: String(
                    cleanedFormData.country_code
                  ).toUpperCase()
                }
              : {})
          }
        ]
      }),
      ...(dataForm.customer_groups_uuids && {
        customer_groups_uuid: dataForm.customer_groups_uuids
      }),
      ...(dataForm.tags?.length && {
        tags: dataForm.tags.map((tag) => tag.id).join(';')
      }),
      ...(metadata !== null && {
        metadata: metadata
      }),
      ...(dataForm.hasContract &&
        dataForm.contractModelUuid && {
          contract_model_uuid: dataForm.contractModelUuid,
          ...(dataForm.contractEndDate && {
            contract_end_date: dataForm.contractEndDate
          })
        })
    }

    const customerInfoAttributes: Record<string, any> = {}

    if (tributaryRegimeValue) {
      const parsedValue = Number(tributaryRegimeValue)
      if (!Number.isNaN(parsedValue)) {
        customerInfoAttributes.tributary_regime = parsedValue
      }
    }

    if (dataForm.municipal_registration) {
      customerInfoAttributes.municipal_registration =
        dataForm.municipal_registration
    }

    if (Object.keys(customerInfoAttributes).length > 0) {
      data.customer_info_attributes = customerInfoAttributes
    }

    data.custom_field_values = customFieldValues

    return { data, cleanedFormData }
  }

  const submitForm = async (dataForm: CustomersBodyModalForm) => {
    !!onSubmit && onSubmit(dataForm)
    const id = toast.loading('Cadastrando o cliente...')

    const { data, cleanedFormData } = buildCustomerPayload(dataForm)

    const response = await DataService({
      data,
      type: 'POST',
      url: '/v3/customers'
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    !!setRefresh && setRefresh((prevState) => !prevState)
    if (!response.error) {
      // SC-10694: primeiro marco de ativação. Em agosto, 51 de 159 contas novas
      // criaram um cliente, mas só 21 chegaram a criar cobrança — medir os dois
      // separadamente mostra onde a jornada trava de verdade.
      // `origin` distingue a criação pelo autocomplete (durante o fluxo de
      // cobrança) da criação direta na tela de clientes.
      track('customer_created', {
        category: data?.category,
        has_address: !!data?.addresses_attributes,
        origin: isFromAutocomplete ? 'autocomplete' : 'customers_screen',
        is_mobile: isMobileViewport()
      })

      if (isFromAutocomplete && onCustomerCreated) {
        onCustomerCreated({
          uuid: response.data.record.uuid,
          name: cleanedFormData.name || '',
          document: cleanedFormData.document,
          email: cleanedFormData.email,
          phone: cleanedFormData.phone
        })
        onClose(true)
        reset()
      } else {
        openCreatedCustomer(response.data.record.uuid)
        onClose(true)
        reset()
      }
    }

    setLoadFetch(false)
  }

  const finishWithCustomer = (
    uuid: string,
    source: { name?: string; document?: string; email?: string; phone?: string }
  ) => {
    !!setRefresh && setRefresh((prevState) => !prevState)

    if (isFromAutocomplete && onCustomerCreated) {
      onCustomerCreated({
        uuid,
        name: source.name || '',
        document: source.document,
        email: source.email,
        phone: source.phone
      })
    } else {
      openCreatedCustomer(uuid)
    }

    onClose(true)
    reset()
  }

  const fetchExistingCustomer = async (uuid: string) => {
    const response = await DataService({
      url: `/v1/customers/${uuid}`,
      type: 'GET'
    } as DataServiceProps)

    if (response?.error || !response?.data?.record) return null
    return response.data.record
  }

  // Devolve o valor atual de um campo extra num formato que o
  // CustomFieldValuesService aceita de volta sem corromper o dado.
  //
  // O encoder devolve `field_value` já formatado, e nem todo formato faz
  // round-trip: upload vira o objeto do arquivo (o service espera o uuid) e radio
  // legado em texto puro seria re-serializado como JSON de string.
  const preservableCustomValue = (field: any) => {
    const value = field?.field_value

    if (isEmptyCustomValue(value)) return null

    if (field.field_type === 'upload') {
      // Objeto do arquivo (ou array deles, no campo com vários arquivos) volta
      // como uuid (ou array de uuids)
      return normalizeUploadSubmitValue(value)
    }

    if (field.field_type === 'radio' && typeof value !== 'object') {
      // O service grava radio como `value.to_json`, e o encoder só desserializa quando o
      // valor começa com [ ou {. Um radio no formato legado chega aqui como JSON de
      // string ('"FORTALEZA"'), não como texto cru — em produção são 419 de 1.582, e
      // nenhum é cru de verdade. Devolver o valor JÁ PARSEADO reproduz byte a byte o que
      // está gravado; devolver a string como veio embrulharia o JSON mais uma vez.
      try {
        return JSON.parse(value)
      } catch {
        // Texto realmente cru: reconstrói a opção para não gravar JSON de string.
        const options = customFields.find((f) => f.uuid === field.uuid)?.options
        const option = options?.find((opt) => opt.value === value)
        return option
          ? { label: option.label, value: option.value }
          : { label: String(value), value: String(value) }
      }
    }

    return value
  }

  // O update roda o service com delete_missing_fields: true, e o service marca o
  // campo como processado mesmo quando o valor vem vazio — ou seja, mandar o
  // payload de criação cru apagaria todo campo extra que o usuário não preencheu.
  // Aqui o que foi digitado vence e o que ficou em branco volta com o valor atual.
  const mergeCustomFieldValues = (
    existingFields: any[],
    typedValues: Array<{ custom_field_uuid: string; value: any }>
  ) => {
    const typedMap = new Map(
      typedValues.map((item) => [item.custom_field_uuid, item.value])
    )
    const merged: Array<{ custom_field_uuid: string; value: any }> = []
    const handled = new Set<string>()

    existingFields.forEach((field) => {
      const uuid = field?.uuid
      if (!uuid) return
      // accept nunca é alterado nem apagado pelo service, e o modal não o exibe.
      if (field.field_type === 'accept') return

      handled.add(uuid)

      const typedValue = typedMap.get(uuid)
      if (!isEmptyCustomValue(typedValue)) {
        merged.push({ custom_field_uuid: uuid, value: typedValue })
        return
      }

      const preserved = preservableCustomValue(field)
      if (preserved !== null) {
        merged.push({ custom_field_uuid: uuid, value: preserved })
      }
    })

    typedValues.forEach(({ custom_field_uuid, value }) => {
      if (handled.has(custom_field_uuid)) return
      if (isEmptyCustomValue(value)) return
      merged.push({ custom_field_uuid, value })
    })

    return merged
  }

  // Mescla no front, não no backend: o PATCH /v3/customers/:uuid é o mesmo da tela
  // de edição de cliente, onde "apaga o que não veio" é o comportamento correto.
  // Mandando o conjunto completo, nenhum dos caminhos destrutivos dispara.
  const buildMergedUpdatePayload = (
    existing: any,
    dataForm: CustomersBodyModalForm
  ) => {
    const { data } = buildCustomerPayload(dataForm)
    const merged: any = {}

    // Preencheu, atualiza; não preencheu, mantém o que já está lá.
    ;['name', 'nickname', 'email', 'phone', 'document'].forEach((key) => {
      merged[key] = isFilled(data[key]) ? data[key] : existing[key]
    })

    // category e foreign NÃO vão no update. Os dois nascem de default do formulário
    // de criação, não de escolha do usuário: is_lead nasce false, então data.category
    // é sempre 'active_customer', e o dialog só abre para documento nacional (o
    // can_create devolve can_create: true e sai antes quando foreign), então
    // data.foreign é sempre false. Enviá-los reclassificava o cadastro existente sem
    // ninguém ter pedido — um cliente 'lead' virava 'active_customer' e, pior,
    // ganhava category_dirty: true na api, que é o que tira o cadastro da
    // recategorização automática do CustomersCategorizeBatchWorker para sempre.
    // Reaproveitar um cadastro não é reclassificá-lo: quem quiser mudar categoria
    // muda na tela do cliente.

    // Endereço só vai quando o usuário preencheu. O callback check_only_one_active
    // do model já inativa o anterior, então substituir é o comportamento nativo.
    if (data.addresses_attributes) {
      merged.addresses_attributes = data.addresses_attributes
    }

    const existingInfo = existing.customer_info || {}
    const typedInfo = data.customer_info_attributes || {}
    const customerInfo: Record<string, any> = {}
    const municipalRegistration =
      typedInfo.municipal_registration ?? existingInfo.municipal_registration
    const tributaryRegime =
      typedInfo.tributary_regime ?? existingInfo.tributary_regime
    if (isFilled(municipalRegistration)) {
      customerInfo.municipal_registration = municipalRegistration
    }
    if (isFilled(tributaryRegime)) {
      customerInfo.tributary_regime = tributaryRegime
    }
    if (Object.keys(customerInfo).length > 0) {
      merged.customer_info_attributes = customerInfo
    }

    // process_tags é substituição, não soma: mandar só as tags digitadas apagaria as
    // que o cliente já tem. Manda-se a união (sem nenhuma, a chave fica de fora e o
    // back mantém as tags como estão).
    const existingTagUuids = (existing.tags || [])
      .map((tag: any) => tag?.uuid)
      .filter(Boolean)
    const typedTagUuids = data.tags ? String(data.tags).split(';') : []
    const allTagUuids = Array.from(
      new Set([...existingTagUuids, ...typedTagUuids])
    ).filter(Boolean)
    if (allTagUuids.length > 0) {
      merged.tags = allTagUuids.join(';')
    }

    // Metadata: a chave presente faz o update destruir e recriar tudo, então ou vai
    // o conjunto completo, ou não vai chave nenhuma.
    //
    // `record.metadata` já vem ACHATADO do encoder ({ erp_id: '...' }), não embrulhado
    // no registro Metadata. É o mesmo contrato que a tela de edição de cliente consome
    // em Data.tsx com Object.entries(customer.metadata).
    const existingMetadata = existing.metadata || {}
    const mergedMetadata = { ...existingMetadata, ...(data.metadata || {}) }
    if (Object.keys(mergedMetadata).length > 0) {
      merged.metadata = mergedMetadata
    }

    // Grupos são aditivos no update (ver V3::CustomersController#update).
    if (data.customer_groups_uuid) {
      merged.customer_groups_uuid = data.customer_groups_uuid
    }

    merged.custom_field_values = mergeCustomFieldValues(
      existing.custom_fields || [],
      data.custom_field_values || []
    )

    return merged
  }

  const activateCustomer = async (uuid: string) => {
    const response = await DataService({
      url: `/v3/customers/${uuid}/unarchive`,
      type: 'POST'
    } as DataServiceProps)

    return response
  }

  const runDialogAction = async ({
    uuid,
    dataForm,
    activate,
    update
  }: {
    uuid: string
    dataForm: CustomersBodyModalForm
    activate: boolean
    update: boolean
  }) => {
    setDuplicateDialogLoading(true)
    const conflictRecord = duplicateDialog.records.find(
      (record) => record.uuid === uuid
    )
    const toastId = toast.loading(
      activate
        ? update
          ? 'Reativando e atualizando o cadastro...'
          : 'Reativando o cadastro...'
        : 'Atualizando o cadastro...'
    )
    let activationMessage = ''

    try {
      if (activate) {
        const activation = await activateCustomer(uuid)
        if (activation?.error) {
          toast.update(toastId, {
            render:
              activation.detail_error ||
              activation.message ||
              'Não foi possível reativar este cadastro.',
            type: 'error',
            ...toastProps
          })
          return
        }
        activationMessage = activation?.data?.message || ''
      }

      if (!update) {
        // A copy certa (reativado x desarquivado) vem do backend, que sabe de qual
        // status o cadastro saiu.
        toast.update(toastId, {
          render: activationMessage || 'Cadastro reativado com sucesso.',
          type: 'success',
          ...toastProps
        })
        closeDuplicateDialog()
        finishWithCustomer(uuid, conflictRecord ?? dataForm)
        return
      }

      const existing = await fetchExistingCustomer(uuid)
      if (!existing) {
        toast.update(toastId, {
          render: 'Não foi possível carregar o cadastro para atualizar.',
          type: 'error',
          ...toastProps
        })
        return
      }

      const data = buildMergedUpdatePayload(existing, dataForm)
      const response = await DataService({
        data,
        type: 'PATCH',
        url: `/v3/customers/${uuid}`
      })

      if (response?.error) {
        toast.update(toastId, {
          render:
            response.detail_error ||
            response.message ||
            'Houve um erro ao atualizar o cliente.',
          type: 'error',
          ...toastProps
        })
        return
      }

      toast.update(toastId, {
        render: activate
          ? 'Cadastro reativado e atualizado com sucesso.'
          : 'Cliente atualizado com sucesso.',
        type: 'success',
        ...toastProps
      })
      closeDuplicateDialog()
      // O registro devolvido pelo PATCH é o que tem os dados finais: se o usuário
      // deixou campos em branco, o formulário sozinho devolveria nome vazio para o
      // autocomplete da cobrança.
      finishWithCustomer(uuid, response?.data?.record ?? dataForm)
    } finally {
      setDuplicateDialogLoading(false)
    }
  }

  const duplicateIsInactive = duplicateDialog.reason === 'inactive_customer'

  const duplicateHasDeleted = duplicateDialog.records.some(
    (record) => record.status === 'deleted'
  )

  const selectedConflictIsInactive = duplicateDialog.records.some(
    (record) =>
      record.uuid === selectedConflictUuid &&
      INACTIVE_STATUSES.includes(record.status)
  )

  const duplicateDialogTitle = duplicateIsInactive
    ? 'Cadastro inativo'
    : 'Cliente já cadastrado'

  const buildDuplicateDescription = () => {
    if (duplicateIsInactive) {
      return 'Esse documento já pertence a um cadastro que não está ativo.'
    }
    if (duplicateDialog.records.length > 1) {
      return 'Já existem clientes cadastrados com esse documento.'
    }
    return 'Já existe um cliente cadastrado com esse documento.'
  }

  const duplicateDialogDescription = buildDuplicateDescription()

  const buildInactiveWarning = () => {
    if (!duplicateIsInactive) return ''

    // Com mais de um registro o aviso não tem como estar certo: ele fala de "o
    // cadastro" no singular enquanto a lista mostra vários, e num conjunto misto
    // (um excluído, um arquivado) afirma um status que vale só para parte dos cards.
    // O "· Excluído"/"· Arquivado" de cada card já diz o que o aviso diria, por card.
    if (duplicateDialog.records.length > 1) return ''

    if (duplicateHasDeleted) {
      // Só o estado, sem explicar o efeito colateral: Customer#delete cancela
      // cobranças pendentes e recorrências ativas e Customer#active não desfaz nada
      // disso, mas falar de cancelamento aqui, antes de o usuário ter escolhido
      // reativar, lia como se a reativação fosse cancelar algo agora.
      return 'O cadastro foi excluído.'
    }
    return 'O cadastro está arquivado.'
  }

  const duplicateInactiveWarning = buildInactiveWarning()

  // Contrato e convite só existem no create. Reusá-los no update mandaria um
  // segundo contrato para assinatura ou resetaria o acesso de quem já usa, então
  // ficam de fora — mas o usuário precisa saber antes de escolher.
  const buildSkipWarnings = () => {
    const form = duplicateDialog.dataForm
    const warnings: string[] = []
    if (form?.hasContract && form?.contractModelUuid) {
      warnings.push('o contrato não é gerado')
    }
    if (form?.create_user) {
      warnings.push('o convite de acesso não é enviado')
    }
    return warnings
  }

  const duplicateSkipWarnings = buildSkipWarnings()

  const handleDuplicateDialogAction = async ({
    activate,
    update
  }: {
    activate: boolean
    update: boolean
  }) => {
    const dataForm = duplicateDialog.dataForm
    if (!dataForm || !selectedConflictUuid) return

    await runDialogAction({
      uuid: selectedConflictUuid,
      dataForm,
      activate,
      update
    })
  }

  // Só aparece para merchant com allow_duplicate_customers ligada, e só no dialog
  // de cadastro inativo: a flag libera duplicar entre cadastros vivos, não criar
  // cópia órfã de um excluído sem o usuário ver a opção de reativar.
  const handleCreateAnyway = async () => {
    const dataForm = duplicateDialog.dataForm
    if (!dataForm) return

    closeDuplicateDialog()
    setLoadFetch(true)
    isSubmittingRef.current = true
    await validateContractFieldsAndSubmit(dataForm)
  }

  const handleValidateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    handleStateError(undefined)
    isSubmittingRef.current = true
    awaitForSubmitCEP.cancel()

    handleSubmit(handleSubmitForm, (errors) => {
      toastFirstError(errors)
      isSubmittingRef.current = false
    })()
  }

  const validateContractFieldsAndSubmit = async (
    dataForm: CustomersBodyModalForm
  ) => {
    const customer_groups_uuids = dataForm.groupsValue?.length
      ? dataForm.groupsValue.map(({ value }) => value).join(';')
      : ''

    // Check custom fields with fresh form values
    const emptyCustom = getEmptyCustomFields(dataForm)
    setContractEmptyCustomFields(emptyCustom)

    const missingFieldLabels = [
      ...contractMissingStandardFields.map((f) => f.label),
      ...contractMissingCustomFields.map((f) => f.title),
      ...emptyCustom.map((f) => f.title)
    ]

    const hasMissingFields =
      watchedContractModelUuid && missingFieldLabels.length > 0
    const hasBillingFields =
      watchedContractModelUuid && contractBillingFields.length > 0

    if (hasMissingFields || hasBillingFields) {
      const allLabels = [...missingFieldLabels, ...contractBillingFields]
      setContractDialogMissingLabels(allLabels)

      const description = hasMissingFields
        ? 'Os seguintes campos são utilizados no modelo de contrato e não foram preenchidos:'
        : 'Este modelo utiliza variáveis de cobrança/recorrência que só são preenchidas via link de pagamento:'

      setDialog({
        isOpen: true,
        title: 'O contrato ficará como rascunho',
        description,
        customerUuid: '',
        action: async () => {
          setDialog(dialogDefaultValues)
          setContractDialogMissingLabels([])
          setLoadFetch(true)
          await submitForm({ ...dataForm, customer_groups_uuids })
          setLoadFetch(false)
          isSubmittingRef.current = false
        }
      })
      // Set errors on all missing fields so they show red
      if (hasMissingFields) {
        checkStandardFieldValues()
        emptyCustom.forEach((cf) => {
          setError(`extraField_${cf.uuid}` as any, {
            type: 'contract',
            message: 'Campo necessário para o contrato'
          })
        })
      }
      setLoadFetch(false)
      isSubmittingRef.current = false
      return
    }

    await submitForm({ ...dataForm, customer_groups_uuids })
    setLoadFetch(false)
    isSubmittingRef.current = false
  }

  const handleSubmitForm: SubmitHandler<CustomersBodyModalForm> = async (
    dataForm
  ) => {
    setLoadFetch(true)

    // Manda o documento cru: o can_create expande para [cru, normalizado], o mesmo
    // par que Customer#document_unique_per_merchant compara. Normalizar aqui faria
    // o pre-check divergir da validação que barra de fato.
    const check = await checkCanCreate(
      dataForm.document || '',
      !!dataForm.foreign
    )

    if (!check.canCreate && check.records.length > 0) {
      setContractDialogMissingLabels([])
      setSelectedConflictUuid(
        check.records.length === 1 ? check.records[0].uuid : ''
      )
      setDuplicateDialog({ ...check, isOpen: true, dataForm })
      setLoadFetch(false)
      isSubmittingRef.current = false
      return
    }

    await validateContractFieldsAndSubmit(dataForm)
  }
  const getGroups = useCallback(async () => {
    const response = await DataService({
      url: '/v1/customer_groups/autocomplete',
      type: 'GET'
    })

    if (response.status === 200) {
      const groupsList: AutoCompleteBillingsParams[] =
        response.data.records.map(toGroupOption)

      setGroups(groupsList)
      return groupsList
    }
    return null
  }, [])
  const getCEP = async (cep: string) => {
    if (isSubmittingRef.current) return
    setCepLoading(true)
    try {
      const response = await getCEPData(cep)

      if (response.error) {
        const errorMessage = response.message || 'CEP inválido'
        setCepError(errorMessage)
        setError('postalCode', {
          message: errorMessage
        })
        setValue('state', undefined)
        handleStateValue(null)
        setValue('city', '')
        setValue('street', '')
        setValue('neighborhood', '')
        return
      }

      const { localidade, logradouro, bairro, uf } = response
      if (localidade || logradouro || bairro) {
        setCepError(undefined)
        clearErrors('postalCode')
        setValues(
          {
            ...(!!localidade && { city: localidade }),
            ...(!!bairro && { neighborhood: bairro }),
            ...(!!logradouro && { street: logradouro })
          },
          setValue
        )
        const stateOption = UFOptions.filter(
          (cepItem) => cepItem.value === uf
        )[0]
        setValue('state', stateOption)
        handleStateValue(stateOption)
        handleStateError(undefined)
        clearErrors('city')
        clearErrors('state')
        clearErrors('street')
        clearErrors('neighborhood')
      } else {
        setValue('state', undefined)
        handleStateValue(null)
        setValue('city', '')
        setValue('street', '')
        setValue('neighborhood', '')
      }
    } catch (err) {
      return
    } finally {
      setCepLoading(false)
    }
  }

  const awaitForSubmitCEP = Debouncer(getCEP, 1000)

  const handleGetCustomerByDocument = async () => {
    if (
      !document ||
      normalizeDocument(document || '') ===
        normalizeDocument(documentOptions.document)
    )
      return

    // Guarda de reentrância: enquanto uma consulta está em andamento, ignora
    // novos disparos. Sem isso, cada clique enfileirava uma requisição nova
    // (o botão só era liberado no fim do await, e nunca em caso de erro).
    if (documentOptions.loading) return

    setDocumentOptions((s) => ({ ...s, loading: true }))
    try {
      const url = `/v1/customers/enrichment/${normalizeDocument(
        document || ''
      )}`
      const response = await DataService({ type: 'GET', url })

      // Quota mensal de consultas de enriquecimento esgotada (SC-10229). O
      // DataService não faz throw em erro HTTP — devolve
      // { error, message, status } — então sem este tratamento o 429 seguia
      // pelo caminho de sucesso e o merchant não via nada. Não marca o
      // documento como consultado: a consulta precisa voltar a funcionar assim
      // que o suporte liberar a quota, sem trocar de documento.
      if (response?.status === 429) {
        toast.error(
          response?.message ||
            'Quota de enriquecimento esgotada. Entre em contato com o suporte.',
          { toastId: 'enrichment-quota-reached', autoClose: 10000 }
        )
        setDocumentOptions((s) => ({ ...s, loading: false }))
        return
      }

      // Qualquer outra falha (400 do rescue do controller, timeout da
      // BigDataCorp, falha de rede): libera o botão sem marcar o documento como
      // consultado, permitindo nova tentativa manual — mesmo contrato do catch
      // abaixo, que o DataService nunca dispara para erro HTTP. Sem esta guarda,
      // a leitura opcional de `record` trataria falha como "não encontrado" e o
      // guard de reentrância mataria o botão para aquele documento.
      if (response?.status !== 200) {
        setDocumentOptions((s) => ({ ...s, loading: false }))
        return
      }

      const record = response?.data?.record

      // CPF de menor de idade (SC-11797): a BigDataCorp não devolve dados sem
      // a data de nascimento. Avisa o merchant para preencher à mão, em vez de
      // simplesmente não preencher nada.
      if (response?.data?.reason === 'minor_document') {
        toast.warning(
          response?.data?.message ||
            'Não é possível buscar dados de CPF de menor de idade. Preencha os dados manualmente.',
          { toastId: 'enrichment-minor-document', autoClose: 10000 }
        )
      }

      // Consulta bem-sucedida sem retorno: marca o documento como consultado,
      // sem sobrescrever o que o merchant preencheu à mão (antes, a UF
      // selecionada era limpa a cada consulta sem resultado).
      if (!record) {
        setDocumentOptions({
          loading: false,
          document: normalizeDocument(document || '')
        })
        return
      }

      const {
        state,
        ...user
      }: Omit<CustomersBodyModalForm, 'state'> & { state: string } = record
      // Mesma regra da busca por CEP: só preenche o que veio. A BigDataCorp manda
      // null no que não tem (ex.: identificador interno de CPF sem apelido), e
      // null num campo yup.string() trava o formulário com "must be a `string`
      // type, but the final value was: `null`".
      setValues(
        Object.fromEntries(
          Object.entries(user).filter(
            ([, value]) => value !== null && value !== undefined && value !== ''
          )
        ) as typeof user,
        setValue
      )
      record.postal_code && setValue('postalCode', record.postal_code)
      record.number && setValue('numberHome', record.number)
      const stateOption =
        UFOptions.filter((cep) => cep.value === state)[0] || null
      setValue('state', stateOption)
      handleStateValue(stateOption)
      if (stateOption) {
        handleStateError(undefined)
      }
      setDocumentOptions({
        loading: false,
        document: normalizeDocument(document || '')
      })
    } catch (err) {
      // Falha na consulta (timeout da BigDataCorp, rede, etc): libera o botão
      // sem marcar o documento como consultado, permitindo nova tentativa
      // manual — mas apenas uma por vez.
      setDocumentOptions((s) => ({ ...s, loading: false }))
    }
  }

  const getContractModels = useCallback(async () => {
    try {
      const response = await DataService({
        url: '/v1/contract_models/',
        type: 'GET'
      })
      if (response.status === 200) {
        setContractModels(
          response.data.records.map((r: any) => ({
            label: r.title,
            value: r.uuid
          }))
        )
      }
    } catch (error) {
      console.error('Erro ao carregar modelos de contrato:', error)
    }
  }, [])

  const checkContractRequiredFields = useCallback(
    async (contractModelUuid: string) => {
      if (!contractModelUuid) {
        setContractMissingCustomFields([])
        setContractMissingStandardFields([])
        setContractRequiredStandardFields([])
        setContractEmptyCustomFields([])
        setContractRequiredCustomFields([])
        setContractBillingFields([])
        return
      }

      try {
        const response = await DataService({
          url: `/v1/contract_models/${contractModelUuid}/required_fields`,
          type: 'GET'
        })

        if (response.status === 200 && response.data) {
          // Standard customer fields
          setContractRequiredStandardFields(response.data.customer_fields || [])

          // Custom fields
          const contractCustomFields = (response.data.custom_fields ||
            []) as Array<{
            uuid: string
            title: string
            field_type: string
          }>

          const formFieldUuids = [
            ...customFields.map((f) => f.uuid),
            ...items.map((f) => f.uuid)
          ]

          const missingCustom = contractCustomFields
            .filter((cf) => !formFieldUuids.includes(cf.uuid))
            .map((cf) => ({
              uuid: cf.uuid,
              title: cf.title,
              field_type: cf.field_type
            }))

          setContractMissingCustomFields(missingCustom)
          setContractRequiredCustomFields(contractCustomFields)

          // Billing/recurrence fields
          setContractBillingFields(response.data.billing_fields || [])
        }
      } catch (error) {
        console.error('Erro ao verificar campos do contrato:', error)
      }
    },
    [customFields, items]
  )

  const contractFieldToFormFields: Record<string, string[]> = useMemo(
    () => ({
      name: ['name'],
      nickname: ['nickname'],
      document: ['document'],
      email: ['email'],
      phone: ['phone'],
      address: ['postalCode', 'street', 'city']
    }),
    []
  )

  const checkStandardFieldValues = useCallback(() => {
    if (contractRequiredStandardFields.length === 0) {
      setContractMissingStandardFields([])
      return
    }

    const formValues = watch()
    const fieldChecks: Record<string, boolean> = {
      name: !!formValues.name?.trim(),
      nickname: !!formValues.nickname?.trim(),
      document: !!formValues.document?.trim(),
      email: !!formValues.email?.trim(),
      phone: !!formValues.phone?.trim(),
      address: !!(
        formValues.postalCode?.trim() ||
        formValues.street?.trim() ||
        formValues.city?.trim()
      )
    }

    const missing = contractRequiredStandardFields.filter(
      (f) => !fieldChecks[f.field]
    )
    setContractMissingStandardFields(missing)

    // Set/clear errors on form fields to show native red border
    contractRequiredStandardFields.forEach((reqField) => {
      const formFields = contractFieldToFormFields[reqField.field] || []
      const isMissing = !fieldChecks[reqField.field]
      formFields.forEach((formField) => {
        if (isMissing) {
          setError(formField as any, {
            type: 'contract',
            message: `Campo necessário para o contrato`
          })
        } else {
          clearErrors(formField as any)
        }
      })
    })
  }, [
    contractRequiredStandardFields,
    watch,
    setError,
    clearErrors,
    contractFieldToFormFields
  ])

  const getEmptyCustomFields = useCallback(
    (formData: Record<string, any>) => {
      const formFieldUuids = [
        ...customFields.map((f) => f.uuid),
        ...items.map((f) => f.uuid)
      ]

      return contractRequiredCustomFields
        .filter((cf) => formFieldUuids.includes(cf.uuid))
        .filter((cf) => {
          const val = formData[`extraField_${cf.uuid}`]
          if (val === undefined || val === null || val === '') return true
          if (Array.isArray(val) && val.length === 0) return true
          return false
        })
        .map((cf) => ({ uuid: cf.uuid, title: cf.title }))
    },
    [contractRequiredCustomFields, customFields, items]
  )

  const handleAddSingleMissingCustomField = useCallback(
    (field: { uuid: string; title: string; field_type: string }) => {
      const newItem: ExtraFieldItem = {
        uuid: field.uuid,
        name: field.title,
        required: true,
        field_type: field.field_type,
        options: []
      }

      handleItemsChange([...items, newItem])
      setContractMissingCustomFields((prev) =>
        prev.filter((f) => f.uuid !== field.uuid)
      )
    },
    [items, handleItemsChange]
  )

  const handleAddMissingCustomFields = useCallback(() => {
    const newItems: ExtraFieldItem[] = contractMissingCustomFields.map(
      (field) => ({
        uuid: field.uuid,
        name: field.title,
        required: true,
        field_type: field.field_type,
        options: []
      })
    )

    handleItemsChange([...items, ...newItems])
    setContractMissingCustomFields([])
  }, [contractMissingCustomFields, items, handleItemsChange])

  const watchedContractModelUuid = watch('contractModelUuid')
  const watchedName = watch('name')
  const watchedEmail = watch('email')
  const watchedPhone = watch('phone')
  const watchedDocument = watch('document')
  const watchedNickname = watch('nickname')
  const watchedPostalCode = watch('postalCode')
  const watchedStreet = watch('street')
  const watchedCity = watch('city')

  useEffect(() => {
    if (watchedContractModelUuid) {
      checkContractRequiredFields(watchedContractModelUuid)
    } else {
      setContractMissingCustomFields([])
      setContractMissingStandardFields([])
      setContractRequiredStandardFields([])
      setContractEmptyCustomFields([])
      setContractRequiredCustomFields([])
      setContractBillingFields([])
      // Clear contract-related errors from form fields
      const allFormFields = Object.values(contractFieldToFormFields).flat()
      allFormFields.forEach((field) => clearErrors(field as any))
    }
  }, [watchedContractModelUuid])

  useEffect(() => {
    if (watchedContractModelUuid) {
      // Recheck custom fields when form custom fields change
      checkContractRequiredFields(watchedContractModelUuid)
    }
  }, [customFields, items])

  useEffect(() => {
    checkStandardFieldValues()
  }, [
    contractRequiredStandardFields,
    watchedName,
    watchedEmail,
    watchedPhone,
    watchedDocument,
    watchedNickname,
    watchedPostalCode,
    watchedStreet,
    watchedCity
  ])

  useEffect(() => {
    getGroups()
    getContractModels()
  }, [])

  // Sincronizar stateValue com defaultValues.state
  useEffect(() => {
    if (defaultValues?.state) {
      handleStateValue(defaultValues.state as StateParams)
    }
  }, [defaultValues?.state])

  // Converter tags quando defaultValues ou availableTags mudarem
  useEffect(() => {
    if (defaultValues && availableTags.length > 0) {
      const formDataWithTags = prepareFormDataWithTags(
        defaultValues,
        availableTags
      )

      // Se há tags para converter, atualizar o formulário
      if (formDataWithTags.tags && formDataWithTags.tags.length > 0) {
        setValue('tags', formDataWithTags.tags)
      }
    }
  }, [defaultValues, availableTags, setValue])

  return (
    <>
      <ModalBody component="form" onSubmit={(e) => handleValidateSubmit(e)}>
        <ModalHeader onClose={() => onClose(false)}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon="faUser" />
            <Typography variant="mdBold">Novo cliente</Typography>
            <Tooltip
              title={
                watch('is_lead')
                  ? 'O cliente será criado na categoria Lead'
                  : 'O cliente será criado na categoria Ativo'
              }
              arrow
            >
              <Box
                onClick={(e) => setCategoryAnchorEl(e.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  padding: '4px 10px',
                  fontWeight: 500,
                  fontSize: '13px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: theme.palette.neutral[800],
                  color: theme.palette.neutral[200],
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.neutral[700]
                  }
                }}
              >
                {watch('is_lead') ? 'Lead' : 'Ativo'}
                <Icon icon="faChevronDown" height="12px" width="12px" />
              </Box>
            </Tooltip>
            <Popover
              anchorEl={categoryAnchorEl}
              open={Boolean(categoryAnchorEl)}
              onClose={() => setCategoryAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted={false}
            >
              <MenuItemsWrapper>
                <StyledMenuItem
                  onClick={() => {
                    setValue('is_lead', false)
                    setCategoryAnchorEl(null)
                  }}
                  selected={!watch('is_lead')}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary[200]
                    }}
                  />
                  Ativo
                </StyledMenuItem>
                <StyledMenuItem
                  onClick={() => {
                    setValue('is_lead', true)
                    setCategoryAnchorEl(null)
                  }}
                  selected={watch('is_lead')}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.secondary[200]
                    }}
                  />
                  Lead
                </StyledMenuItem>
              </MenuItemsWrapper>
            </Popover>
            <Box
              onClick={(e) => setForeignAnchorEl(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '4px 10px',
                fontWeight: 500,
                fontSize: '13px',
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: theme.palette.neutral[800],
                color: theme.palette.neutral[200],
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.neutral[700]
                }
              }}
            >
              {watch('foreign') ? 'Estrangeiro' : 'Brasileiro(a)'}
              <Box sx={{ ml: '16px', display: 'flex' }}>
                <Icon icon="faChevronDown" height="12px" width="12px" />
              </Box>
            </Box>
            <Popover
              anchorEl={foreignAnchorEl}
              open={Boolean(foreignAnchorEl)}
              onClose={() => setForeignAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted={false}
            >
              <MenuItemsWrapper>
                <StyledMenuItem
                  onClick={() => {
                    setValue('foreign', false)
                    setForeignAnchorEl(null)
                  }}
                  selected={!watch('foreign')}
                >
                  Brasileiro(a)
                </StyledMenuItem>
                <StyledMenuItem
                  onClick={() => {
                    setValue('foreign', true)
                    setForeignAnchorEl(null)
                  }}
                  selected={watch('foreign')}
                >
                  Estrangeiro
                </StyledMenuItem>
              </MenuItemsWrapper>
            </Popover>
          </Stack>
        </ModalHeader>

        <ModalContent>
          {companySelector}
          <Typography color={theme.palette.neutral[200]} variant="mdBold">
            Dados básicos
          </Typography>
          <InputController sx={{ mb: 1 }} control={control} name="document">
            <Textfield
              label={
                <Stack
                  sx={{ marginBottom: '-15px' }}
                  direction="row"
                  flexWrap="nowrap"
                >
                  <p>{watch('foreign') ? 'Documento' : 'CPF/CNPJ'}</p> <p>*</p>
                </Stack>
              }
              mask={watch('foreign') ? undefined : 'cpfCnpj'}
              placeholder={
                watch('foreign') ? 'Digite o documento' : 'Digite o CPF ou CNPJ'
              }
              sx={{
                '& .MuiInputBase-input::placeholder': {
                  color: theme.palette.neutral[400],
                  opacity: 1
                }
              }}
              customEndAdornment={
                watch('foreign') ? undefined : (
                  <Button
                    disabled={documentOptions.loading}
                    onClick={() => handleGetCustomerByDocument()}
                    sx={{
                      borderRadius: '8px',
                      width: '180px',
                      height: '28px',
                      mr: '-6px',
                      ':hover': {
                        height: '28px'
                      }
                    }}
                    variantTypography="smBold"
                    size="small"
                    variantButton="neutral"
                  >
                    <Stack alignItems="center" direction="row" gap="12px">
                      {documentOptions.loading ? (
                        <CircularProgress
                          size={14}
                          sx={{ color: theme.palette.neutral[200] }}
                        />
                      ) : (
                        <SparkleIcon
                          width="14"
                          height="14"
                          color={theme.palette.neutral[200]}
                        />
                      )}

                      {!isMobile && (
                        <Typography noWrap variant="xsBold">
                          {documentOptions.loading
                            ? 'Buscando...'
                            : 'Buscar informações'}
                        </Typography>
                      )}
                    </Stack>
                  </Button>
                )
              }
            />
          </InputController>
          <Grid
            container
            spacing={{ xs: 0, sm: 2 }}
            columns={{ xs: 4, sm: 12 }}
            sx={{ mb: 1 }}
          >
            <Grid item xs={6} my={1}>
              <InputController
                control={control}
                name="name"
                autoComplete="new-password"
                placeholder="Digite o nome completo"
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: theme.palette.neutral[400],
                    opacity: 1
                  }
                }}
              >
                <Textfield label="Nome completo*" />
              </InputController>
            </Grid>

            <Grid item xs={6} my={1}>
              <div>
                <InputController
                  control={control}
                  name="nickname"
                  autoComplete="new-password"
                >
                  <Textfield
                    sx={{
                      '& .MuiInputBase-input::placeholder': {
                        color: theme.palette.neutral[400],
                        opacity: 1
                      }
                    }}
                    placeholder="Digite o identificador interno"
                    label={
                      <Box display="flex" alignItems="center" gap="4px">
                        {f('form_data.surname')}
                        <Tooltip
                          enterTouchDelay={0}
                          title="Este campo é destinado para adicionar informações complementares, exemplo: nome de outro responsável, nome do dependente, código de identificação, etc."
                        >
                          <Box display="flex" alignItems="center">
                            <Icon
                              colorSX={theme.palette.neutral[700]}
                              icon="help"
                              height="15px"
                              width="15px"
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    }
                  />
                </InputController>
              </div>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={{ xs: 0, sm: 2 }}
            columns={{ xs: 4, sm: 12 }}
          >
            <Grid item={true} xs={6}>
              <InputController
                control={control}
                name="email"
                autoComplete="new-password"
              >
                <Textfield
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      color: theme.palette.neutral[400],
                      opacity: 1
                    }
                  }}
                  placeholder="Digite o e-mail"
                  label="Email"
                />
              </InputController>
            </Grid>
            <Grid item={true} xs={6}>
              <Controller
                control={control}
                name={'phone'}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { error }
                }) => (
                  <PhoneInput
                    country={watch('foreign') ? undefined : 'br'}
                    enableSearch={!!watch('foreign')}
                    placeholder={
                      watch('foreign')
                        ? 'Telefone com DDI'
                        : '+55 (DDD) 90000-0000'
                    }
                    errorLabel={error ? error.message : undefined}
                    value={value as string}
                    onBlur={onBlur}
                    onChange={(val: string, data: any) => {
                      onChange(val)
                      if (watch('foreign') && data?.countryCode) {
                        setValue('country_code', data.countryCode)
                      }
                    }}
                    label="Telefone"
                  />
                )}
              />
            </Grid>
          </Grid>
          {watch('foreign') && (
            <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="country_code"
                  render={({ field: { onChange, value } }) => {
                    const selectedCountry =
                      COUNTRY_OPTIONS.find(
                        (c) => c.code === String(value || '').toLowerCase()
                      ) || null
                    return (
                      <Autocomplete
                        options={COUNTRY_OPTIONS}
                        getOptionLabel={(o: any) => o?.name || ''}
                        isOptionEqualToValue={(o: any, v: any) =>
                          o?.code === v?.code
                        }
                        value={selectedCountry}
                        onChange={(_: any, opt: any) =>
                          onChange(opt?.code || '')
                        }
                        renderOption={(props: any, option: any) => (
                          <Box
                            component="li"
                            {...props}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '14px',
                              color: theme.palette.neutral[200]
                            }}
                          >
                            <Box
                              component="img"
                              loading="lazy"
                              src={`https://flagcdn.com/w20/${option.code}.png`}
                              srcSet={`https://flagcdn.com/w40/${option.code}.png 2x`}
                              alt=""
                              sx={{ width: 20, height: 14, flexShrink: 0 }}
                            />
                            {option.name}
                          </Box>
                        )}
                        renderInput={(params: any) => (
                          <Textfield
                            {...params}
                            label="País*"
                            placeholder="Selecione o país"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: selectedCountry ? (
                                <Box
                                  component="img"
                                  loading="lazy"
                                  src={`https://flagcdn.com/w20/${selectedCountry.code}.png`}
                                  srcSet={`https://flagcdn.com/w40/${selectedCountry.code}.png 2x`}
                                  alt=""
                                  sx={{
                                    width: 20,
                                    height: 14,
                                    ml: 1,
                                    mr: 1,
                                    flexShrink: 0
                                  }}
                                />
                              ) : null
                            }}
                            sx={{
                              '& .MuiInputBase-input::placeholder': {
                                color: theme.palette.neutral[400],
                                opacity: 1
                              }
                            }}
                          />
                        )}
                      />
                    )
                  }}
                />
              </Grid>
            </Grid>
          )}
          <Grid item={true} xs={6} mb={2}>
            <Controller
              control={control}
              name="postalCode"
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => (
                <Textfield
                  helperText={error?.message}
                  error={!!error?.message}
                  label={watch('foreign') ? 'Código postal' : 'CEP'}
                  mask={watch('foreign') ? undefined : 'cep'}
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      color: theme.palette.neutral[400],
                      opacity: 1
                    }
                  }}
                  placeholder={
                    watch('foreign') ? 'Digite o código postal' : 'Digite o CEP'
                  }
                  autoComplete="new-password"
                  onChange={async (event) => {
                    const {
                      target: { value }
                    } = event
                    onChange(value)

                    if (watch('foreign')) return

                    if (onlyNumber(value).length < 8) {
                      setCepError(undefined)
                      clearErrors('postalCode')
                    }

                    if (onlyNumber(value).length === 8) {
                      awaitForSubmitCEP(onlyNumber(value))
                    }
                  }}
                  value={value}
                />
              )}
            />
          </Grid>
          <InputController
            control={control}
            name="street"
            autoComplete="new-password"
            sx={{ mb: 2 }}
          >
            <Textfield
              sx={{
                '& .MuiInputBase-input::placeholder': {
                  color: theme.palette.neutral[400],
                  opacity: 1
                }
              }}
              placeholder="Digite o logradouro"
              label="Logradouro"
            />
          </InputController>
          <Grid container={true} spacing={{ md: 2, xs: 1 }} sx={{ mb: 1 }}>
            <Grid item={true} md={4} xs={12}>
              <InputController
                control={control}
                name="numberHome"
                autoComplete="new-password"
              >
                <Textfield
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      color: theme.palette.neutral[400],
                      opacity: 1
                    }
                  }}
                  placeholder="Digite o número"
                  label="Número"
                />
              </InputController>
            </Grid>
            <Grid item={true} md={4} xs={12}>
              <InputController
                control={control}
                name="complement"
                placeholder="Casa/Apartamento/Comercial"
                autoComplete="new-password"
              >
                <Textfield
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      color: theme.palette.neutral[400],
                      opacity: 1
                    }
                  }}
                  placeholder="Digite o complemento"
                  label="Complemento"
                />
              </InputController>
            </Grid>
            <Grid item={true} md={4} xs={12}>
              {watch('foreign') ? (
                <Controller
                  control={control}
                  name="state"
                  render={({ field: { onChange, value } }) => (
                    <Textfield
                      label="Estado/Região"
                      placeholder="Digite o estado ou região"
                      value={(value as any)?.label || (value as any) || ''}
                      onChange={(e) =>
                        onChange({
                          label: e.target.value,
                          value: e.target.value
                        })
                      }
                    />
                  )}
                />
              ) : (
                <StateAutocomplete
                  error={stateError}
                  label="Estado"
                  setStateError={handleStateError}
                  setStateValue={handleStateValue}
                  stateValue={stateValue}
                />
              )}
            </Grid>
          </Grid>
          <Grid container={true} spacing={2} mt="8px" mb="8px">
            <Grid item={true} xs={6}>
              <InputController
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: theme.palette.neutral[400],
                    opacity: 1
                  }
                }}
                control={control}
                name="neighborhood"
                autoComplete="new-password"
              >
                <Textfield placeholder="Digite o bairro" label="Bairro" />
              </InputController>
            </Grid>
            <Grid item={true} xs={6}>
              <InputController
                control={control}
                name="city"
                autoComplete="new-password"
              >
                <Textfield placeholder="Digite a cidade" label="Cidade" />
              </InputController>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InputController
                control={control}
                name="tributary_regime"
                autoComplete="off"
              >
                <Textfield
                  select
                  label="Regime Tributário"
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      color: theme.palette.neutral[400],
                      opacity: 1
                    }
                  }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) => {
                      if (!selected || selected === '') {
                        return (
                          <Typography
                            color={theme.palette.neutral[400]}
                            fontWeight={300}
                          >
                            Selecione o regime tributário
                          </Typography>
                        )
                      }
                      const option = tributaryRegimeOptions.find(
                        (opt) => opt.value === selected
                      )
                      return option?.label || selected
                    }
                  }}
                >
                  <MenuItem value="">
                    <Typography
                      color={theme.palette.neutral[400]}
                      fontWeight={300}
                    >
                      Selecione o regime tributário
                    </Typography>
                  </MenuItem>
                  {tributaryRegimeOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Textfield>
              </InputController>
            </Grid>
            <Grid item xs={6}>
              <InputController
                control={control}
                name="municipal_registration"
                autoComplete="off"
              >
                <Textfield
                  label="Inscrição Municipal"
                  placeholder="Digite a inscrição municipal"
                />
              </InputController>
            </Grid>
          </Grid>
          <Grid item xs={12} spacing={2} mt="16px" mb="16px">
            <Typography
              color={theme.palette.neutral[200]}
              variant="mdBold"
              sx={{ mt: 3, mb: 2 }}
            >
              Informações extras
            </Typography>
          </Grid>
          <Grid container spacing={3}>
            {customFields
              .filter((field) => field.field_type !== 'accept')
              .map((field) => (
                <Grid item xs={12} key={field.uuid}>
                  <CustomFieldRenderer
                    setUploadIsLoading={setUploadIsLoading}
                    isLoading={uploadIsLoading}
                    field={field}
                    control={control}
                    fieldNamePrefix="extraField"
                    setValue={setValue}
                  />
                </Grid>
              ))}
          </Grid>

          <Grid item xs={12}>
            <ExtraFieldsManager
              uploadIsLoading={uploadIsLoading}
              setUploadIsLoading={setUploadIsLoading}
              excludeFieldTypes={['accept']}
              renderFieldWhenSelected
              control={control}
              setValue={setValue}
              items={items}
              onItemsChange={handleItemsChange}
              title=""
              hasDefaultCustomFields={!!customFields.length}
              addButtonText="Novo campo"
              excludedValues={customFields.map((field) => field.uuid)}
            />
          </Grid>
          <Grid container mt="24px">
            <Grid
              item={true}
              xs={12}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <Controller
                control={control}
                name="create_user"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    onChange={onChange}
                    value={value}
                    checked={value}
                    sx={{ ml: '-10px' }}
                  />
                )}
              />

              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  onClick={() => {
                    setValue('create_user', !watch('create_user'))
                  }}
                  variant="smBold"
                >
                  Enviar e-mail de boas vindas com instruções para primeiro
                  acesso ao Portal.
                </Typography>
                <Tooltip
                  arrow
                  placement="right"
                  title="O seu cliente receberá um email com o link para criar a senha de acesso ao Portal de Clientes - onde poderá ver faturas, documentos e dados cadastrais."
                >
                  <div style={{ display: 'flex', marginTop: '2px' }}>
                    <Icon
                      icon="info"
                      sx={{
                        color: theme.palette.neutral[700],
                        width: '16px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                </Tooltip>
              </Stack>
            </Grid>

            <Grid
              item={true}
              xs={12}
              mt="8px"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <Controller
                name={'groupsValue'}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => (
                  <>
                    <Autocomplete
                      id="group_select"
                      onChange={(_, value) =>
                        onChange(
                          (value as AutoCompleteBillingsParams[]).filter(
                            (opt) => opt.value !== CREATE_NEW_GROUP
                          )
                        )
                      }
                      value={value}
                      options={[
                        { label: '', value: CREATE_NEW_GROUP },
                        ...groups
                      ]}
                      filterOptions={(options, state) =>
                        options.filter(
                          (opt: any) =>
                            opt.value === CREATE_NEW_GROUP ||
                            opt.label
                              ?.toLowerCase()
                              .includes(state.inputValue.toLowerCase())
                        )
                      }
                      sx={{
                        width: '100%'
                      }}
                      autoHighlight
                      getOptionLabel={(option) =>
                        (option as AutoCompleteBillingsParams).label || ''
                      }
                      isOptionEqualToValue={(option, value) =>
                        (option as AutoCompleteBillingsParams).value ===
                        (value as AutoCompleteBillingsParams).value
                      }
                      renderOption={(props, option) => {
                        const optionTyped = option as AutoCompleteBillingsParams
                        if (optionTyped.value === CREATE_NEW_GROUP) {
                          return (
                            <Box
                              component="li"
                              key="create-new-group"
                              {...props}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                setCreateGroupModalOpen(true)
                              }}
                              sx={{
                                width: '100%',
                                borderBottom: `solid 1px ${theme.palette.neutral[700]}`,
                                mt: '-8px'
                              }}
                            >
                              {newGroupButton}
                            </Box>
                          )
                        }
                        return (
                          <GroupOption liProps={props} option={optionTyped} />
                        )
                      }}
                      multiple
                      noOptionsText={
                        <Box
                          onClick={() => setCreateGroupModalOpen(true)}
                          sx={{ cursor: 'pointer', mx: -2, mt: '-3px' }}
                        >
                          {newGroupButton}
                        </Box>
                      }
                      renderInput={(params) => (
                        <Textfield
                          label="Inserir cliente no grupo"
                          helperText={error?.message}
                          error={!!error?.message}
                          placeholder={
                            watch('groupsValue')?.length
                              ? 'Selecione...'
                              : 'Nenhum'
                          }
                          sx={{
                            ['& .MuiInputBase-root']: {
                              height: '100%',
                              minHeight: '36px',
                              padding: '2px'
                            }
                          }}
                          {...params}
                        />
                      )}
                    />

                    <CreateGroupModal
                      open={createGroupModalOpen}
                      onClose={() => setCreateGroupModalOpen(false)}
                      onGroupCreated={async (newGroup) => {
                        const selected = (value || []).filter(
                          (group: AutoCompleteBillingsParams) =>
                            group.value !== newGroup.value
                        )
                        // Já entra selecionado; depois troca pela opção completa
                        // da lista (com cor e participantes) quando ela recarrega.
                        setGroups((prev) => [...prev, newGroup])
                        onChange([...selected, newGroup])

                        const list = await getGroups()
                        const fullGroup = list?.find(
                          (group) => group.value === newGroup.value
                        )
                        if (fullGroup) onChange([...selected, fullGroup])
                      }}
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item={true} xs={12} mt="16px">
              <IdentifyInternal
                control={control}
                setValue={setValue}
                tooltipText="Os Identificadores Internos serão mostrados nos detalhes do seu cliente e apenas você consegue ver esses campos."
              />
            </Grid>

            <Grid item={true} xs={12} mt="16px">
              <Controller
                control={control}
                name="hasContract"
                render={({ field: { onChange, value } }) => (
                  <Accordion
                    expanded={!!value}
                    onChange={() => onChange(!value)}
                    sx={{
                      border: '1px solid #E8EBE8',
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      borderRadius: '8px !important',
                      backgroundColor: '#FAFBFA',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: 0,
                        backgroundColor: '#fff'
                      }
                    }}
                  >
                    <AccordionSummary
                      hasSwitch
                      expandIcon={<Switch checked={!!value} />}
                      sx={{
                        '.MuiAccordionSummary-content': {
                          margin: '12px 0px'
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="smBold">Contrato</Typography>
                        <Tooltip
                          enterTouchDelay={0}
                          arrow
                          placement="top"
                          title="Gere um contrato automaticamente para o cliente ao concluir o cadastro"
                        >
                          <Box>
                            <Icon
                              icon="info"
                              sx={{
                                fontSize: '1.2rem',
                                mt: '3px',
                                color: theme.palette.neutral[600]
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    {value && (
                      <AccordionDetails sx={{ p: 2, pt: 1 }}>
                        <Stack gap={2}>
                          <Controller
                            control={control}
                            name="contractModelUuid"
                            render={({ field, fieldState: { error } }) => (
                              <Autocomplete
                                options={contractModels}
                                value={
                                  contractModels.find(
                                    (m) => m.value === field.value
                                  ) || null
                                }
                                onChange={(_, newValue: any) =>
                                  field.onChange(newValue?.value || null)
                                }
                                getOptionLabel={(option: any) =>
                                  option?.label || ''
                                }
                                isOptionEqualToValue={(
                                  option: any,
                                  value: any
                                ) => option?.value === value?.value}
                                renderInput={(params) => (
                                  <Textfield
                                    label="Modelo de contrato"
                                    placeholder="Selecione um modelo"
                                    helperText={error?.message}
                                    error={!!error?.message}
                                    {...params}
                                  />
                                )}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name="contractEndDate"
                            render={({ field, fieldState: { error } }) => (
                              <Textfield
                                label="Data de vencimento"
                                type="date"
                                helperText={error?.message}
                                error={!!error?.message}
                                {...field}
                                value={field.value || ''}
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                          />
                          {(contractMissingStandardFields.length > 0 ||
                            contractMissingCustomFields.length > 0 ||
                            contractEmptyCustomFields.length > 0) && (
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: '8px',
                                backgroundColor:
                                  theme.palette.warning[800] || '#FFF8E1',
                                border: `1px solid ${
                                  theme.palette.warning[600] || '#FFB74D'
                                }`
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="flex-start"
                                gap={1}
                              >
                                <Icon
                                  icon="info"
                                  sx={{
                                    fontSize: '1.2rem',
                                    mt: '2px',
                                    color:
                                      theme.palette.warning[200] || '#F57C00'
                                  }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="smBold" sx={{ mb: 0.5 }}>
                                    Campos não preenchidos no contrato
                                  </Typography>
                                  <Typography
                                    variant="xsLight"
                                    sx={{
                                      color: theme.palette.neutral[300],
                                      mb: 1
                                    }}
                                  >
                                    O modelo de contrato selecionado utiliza
                                    variáveis que não estão preenchidas. Os
                                    valores ficarão vazios no contrato:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 0.5,
                                      mb: 1
                                    }}
                                  >
                                    {contractMissingStandardFields.map(
                                      (field) => (
                                        <Box
                                          key={field.field}
                                          sx={{
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: '4px',
                                            backgroundColor:
                                              theme.palette.warning[700] ||
                                              '#FFE0B2',
                                            fontSize: '12px',
                                            fontWeight: 600
                                          }}
                                        >
                                          {field.label}
                                        </Box>
                                      )
                                    )}
                                    {contractEmptyCustomFields.map((field) => (
                                      <Box
                                        key={`empty-${field.uuid}`}
                                        sx={{
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: '4px',
                                          backgroundColor:
                                            theme.palette.warning[700] ||
                                            '#FFE0B2',
                                          fontSize: '12px',
                                          fontWeight: 600
                                        }}
                                      >
                                        {field.title}
                                      </Box>
                                    ))}
                                  </Box>
                                  {contractMissingCustomFields.length > 0 && (
                                    <Stack gap={0.5} sx={{ mt: 0.5 }}>
                                      {contractMissingCustomFields.map(
                                        (field) => (
                                          <Stack
                                            key={field.uuid}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                          >
                                            <Typography variant="xsLight">
                                              {field.title} (campo extra)
                                            </Typography>
                                            <Typography
                                              variant="xsBold"
                                              onClick={() =>
                                                handleAddSingleMissingCustomField(
                                                  field
                                                )
                                              }
                                              sx={{
                                                color:
                                                  theme.palette.primary.main,
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                '&:hover': {
                                                  textDecoration: 'underline'
                                                }
                                              }}
                                            >
                                              Adicionar
                                            </Typography>
                                          </Stack>
                                        )
                                      )}
                                      {contractMissingCustomFields.length >
                                        1 && (
                                        <Typography
                                          variant="xsBold"
                                          onClick={handleAddMissingCustomFields}
                                          sx={{
                                            color: theme.palette.primary.main,
                                            cursor: 'pointer',
                                            mt: 0.5,
                                            '&:hover': {
                                              textDecoration: 'underline'
                                            }
                                          }}
                                        >
                                          + Habilitar todos
                                        </Typography>
                                      )}
                                    </Stack>
                                  )}
                                </Box>
                              </Stack>
                            </Box>
                          )}
                          {contractBillingFields.length > 0 && (
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: '8px',
                                backgroundColor:
                                  theme.palette.info[800] || '#E3F2FD',
                                border: `1px solid ${
                                  theme.palette.info[600] || '#64B5F6'
                                }`
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="flex-start"
                                gap={1}
                              >
                                <Icon
                                  icon="info"
                                  sx={{
                                    fontSize: '1.2rem',
                                    mt: '2px',
                                    color: theme.palette.info[200] || '#1976D2'
                                  }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="smBold" sx={{ mb: 0.5 }}>
                                    Contrato ficará como rascunho
                                  </Typography>
                                  <Typography
                                    variant="xsLight"
                                    sx={{
                                      color: theme.palette.neutral[300]
                                    }}
                                  >
                                    Este modelo utiliza variáveis de
                                    cobrança/recorrência que só são preenchidas
                                    via link de pagamento. Ao cadastrar o
                                    cliente por aqui, o contrato será gerado
                                    como rascunho.
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </AccordionDetails>
                    )}
                  </Accordion>
                )}
              />
            </Grid>
          </Grid>
          <Dialog
            icon="add"
            title={dialog.title}
            description={dialog.description}
            setIsOpenDialog={() => handleToggleDialog({})}
            isOpenDialog={dialog.isOpen}
            cancelButton={false}
          >
            <Stack gap={1} width="100%">
              {contractDialogMissingLabels.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}
                  >
                    {contractDialogMissingLabels.map((label) => (
                      <Typography
                        component="li"
                        key={label}
                        variant="smBold"
                        sx={{ color: theme.palette.neutral[200] }}
                      >
                        {label}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              <Button
                onClick={() => {
                  setContractDialogMissingLabels([])
                  handleToggleDialog({})
                }}
                fullWidth
                variantButton="neutral"
              >
                Voltar e preencher
              </Button>
              <Button autoFocus onClick={dialog.action} fullWidth>
                {contractDialogMissingLabels.length > 0
                  ? 'Cadastrar mesmo assim (contrato ficará em rascunho)'
                  : 'Cadastrar mesmo assim'}
              </Button>
            </Stack>
          </Dialog>

          <Dialog
            icon="info"
            title={duplicateDialogTitle}
            description={duplicateDialogDescription}
            setIsOpenDialog={() => dismissDuplicateDialog()}
            isOpenDialog={duplicateDialog.isOpen}
            cancelButton={false}
          >
            <Stack gap={1.5} width="100%">
              {duplicateInactiveWarning && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '6px',
                    backgroundColor: theme.palette.neutral[800]
                  }}
                >
                  <Typography
                    variant="smLight"
                    sx={{ color: theme.palette.neutral[100] }}
                  >
                    {duplicateInactiveWarning}
                  </Typography>
                </Box>
              )}

              {duplicateDialog.records.length > 1 && (
                <Typography
                  variant="smBold"
                  sx={{ color: theme.palette.neutral[200] }}
                >
                  Selecione o cadastro:
                </Typography>
              )}

              <ConflictRecordList
                records={duplicateDialog.records}
                selectedUuid={selectedConflictUuid}
                onSelect={setSelectedConflictUuid}
              />

              {duplicateSkipWarnings.length > 0 && (
                <Typography
                  variant="smLight"
                  sx={{ color: theme.palette.neutral[200] }}
                >
                  {`Ao atualizar um cadastro existente, ${duplicateSkipWarnings.join(
                    ' e '
                  )}. Isso pode ser feito depois, pela tela do cliente.`}
                </Typography>
              )}

              {duplicateIsInactive && !canArchiveCustomers && (
                <Typography
                  variant="smLight"
                  sx={{ color: theme.palette.neutral[200] }}
                >
                  Você não tem permissão para reativar cadastros. Peça a alguém
                  com a permissão de arquivar clientes.
                </Typography>
              )}

              {duplicateIsInactive ? (
                <>
                  {canArchiveCustomers && (
                    <>
                      <Button
                        autoFocus
                        fullWidth
                        loading={duplicateDialogLoading}
                        disabled={
                          duplicateDialogLoading || !selectedConflictIsInactive
                        }
                        onClick={() =>
                          handleDuplicateDialogAction({
                            activate: true,
                            update: true
                          })
                        }
                      >
                        Ativar cadastro e atualizar
                      </Button>
                      <Button
                        fullWidth
                        variantButton="neutral"
                        disabled={
                          duplicateDialogLoading || !selectedConflictIsInactive
                        }
                        onClick={() =>
                          handleDuplicateDialogAction({
                            activate: true,
                            update: false
                          })
                        }
                      >
                        Ativar cadastro
                      </Button>
                    </>
                  )}
                  {duplicateDialog.allowDuplicate && (
                    <Button
                      fullWidth
                      variantButton="neutral"
                      disabled={duplicateDialogLoading}
                      onClick={handleCreateAnyway}
                    >
                      Criar novo cadastro
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  autoFocus
                  fullWidth
                  loading={duplicateDialogLoading}
                  disabled={duplicateDialogLoading || !selectedConflictUuid}
                  onClick={() =>
                    handleDuplicateDialogAction({
                      activate: false,
                      update: true
                    })
                  }
                >
                  Atualizar cadastro
                </Button>
              )}
            </Stack>
          </Dialog>
        </ModalContent>

        <ModalFooter>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
            width="100%"
          >
            <TagsPopover control={control} name="tags" maxTags={10} />

            <Button
              loading={loadFetch || uploadIsLoading || cepLoading}
              disabled={loadFetch || uploadIsLoading || cepLoading}
              type="submit"
              variantButton="primary"
            >
              Salvar
            </Button>
          </Stack>
        </ModalFooter>
      </ModalBody>
    </>
  )
}
